/**
 * Centralized color mapping utilities for consistent styling across the application.
 * This consolidates duplicate color mapping logic that was spread across multiple components.
 */

/**
 * Color palette for status/stage color pickers.
 * Used in ProjectStatusSettings, TaskGroupDialog, and other status configuration UIs.
 * Works well in both light and dark mode.
 */
export const STATUS_PICKER_COLORS = [
  // Grays
  '#6B7280', '#9CA3AF', '#4B5563',
  // Blues
  '#3B82F6', '#60A5FA', '#2563EB',
  // Greens
  '#10B981', '#34D399', '#059669',
  // Yellows/Oranges
  '#F59E0B', '#FBBF24', '#F97316',
  // Reds
  '#EF4444', '#F87171', '#DC2626',
  // Purples
  '#8B5CF6', '#A78BFA', '#7C3AED',
  // Pinks
  '#EC4899', '#F472B6', '#DB2777',
  // Teals
  '#14B8A6', '#2DD4BF', '#0D9488',
];

type Rgb = { r: number; g: number; b: number };

export interface StatusPillStyle {
  backgroundColor: string;
  color: string;
  borderColor: string;
  '--status-pill-bg-dark': string;
  '--status-pill-text-dark': string;
  '--status-pill-border-dark': string;
}

const WHITE: Rgb = { r: 255, g: 255, b: 255 };
const BLACK: Rgb = { r: 0, g: 0, b: 0 };
const DARK_SURFACE: Rgb = { r: 15, g: 23, b: 42 };

const parseHex = (hex: string): Rgb | null => {
  const normalized = hex.trim().replace(/^#/, '');
  const expanded = normalized.length === 3
    ? normalized.split('').map(channel => channel + channel).join('')
    : normalized;

  if (!/^[0-9a-f]{6}$/i.test(expanded)) return null;

  return {
    r: parseInt(expanded.slice(0, 2), 16),
    g: parseInt(expanded.slice(2, 4), 16),
    b: parseInt(expanded.slice(4, 6), 16),
  };
};

const mix = (from: Rgb, to: Rgb, amount: number): Rgb => ({
  r: Math.round(from.r + (to.r - from.r) * amount),
  g: Math.round(from.g + (to.g - from.g) * amount),
  b: Math.round(from.b + (to.b - from.b) * amount),
});

const luminance = ({ r, g, b }: Rgb) => {
  const [red, green, blue] = [r, g, b].map(channel => {
    const value = channel / 255;
    return value <= 0.04045
      ? value / 12.92
      : Math.pow((value + 0.055) / 1.055, 2.4);
  });
  return red * 0.2126 + green * 0.7152 + blue * 0.0722;
};

const contrastRatio = (first: Rgb, second: Rgb) => {
  const lighter = Math.max(luminance(first), luminance(second));
  const darker = Math.min(luminance(first), luminance(second));
  return (lighter + 0.05) / (darker + 0.05);
};

const ensureContrast = (color: Rgb, background: Rgb, target: Rgb, minimumRatio: number) => {
  if (contrastRatio(color, background) >= minimumRatio) return color;
  for (let amount = 0.05; amount <= 1; amount += 0.05) {
    const candidate = mix(color, target, amount);
    if (contrastRatio(candidate, background) >= minimumRatio) return candidate;
  }
  return target;
};

const toRgb = ({ r, g, b }: Rgb) => `rgb(${r} ${g} ${b})`;

/**
 * Derive accessible light and dark pill colors from a persisted status color.
 * The source hex remains the stable identifier; only its UI treatment changes.
 */
export function getStatusPillStyle(hexColor: string): StatusPillStyle | undefined {
  const source = parseHex(hexColor);
  if (!source) return undefined;

  const lightBackground = mix(source, WHITE, 0.9);
  const darkBackground = mix(source, DARK_SURFACE, 0.76);
  const lightText = ensureContrast(source, lightBackground, BLACK, 4.5);
  const darkText = ensureContrast(source, darkBackground, WHITE, 4.5);
  const lightBorder = ensureContrast(source, lightBackground, BLACK, 2);
  const darkBorder = ensureContrast(source, darkBackground, WHITE, 2);

  return {
    backgroundColor: toRgb(lightBackground),
    color: toRgb(lightText),
    borderColor: toRgb(lightBorder),
    '--status-pill-bg-dark': toRgb(darkBackground),
    '--status-pill-text-dark': toRgb(darkText),
    '--status-pill-border-dark': toRgb(darkBorder),
  };
}

/**
 * Tailwind CSS class names for subtle color schemes
 */
export interface SubtleColorScheme {
  bg: string;
  text: string;
  border: string;
}

/**
 * Map of hex colors to their subtle Tailwind CSS equivalents.
 * Used for status badges, tags, and other colored UI elements.
 */
export const HEX_TO_SUBTLE_COLORS: Record<string, SubtleColorScheme> = {
  // Greens
  '#22C55E': { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-700 dark:text-green-400', border: 'border-green-200 dark:border-green-800' },
  '#10B981': { bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800' },
  '#14B8A6': { bg: 'bg-teal-50 dark:bg-teal-900/20', text: 'text-teal-700 dark:text-teal-400', border: 'border-teal-200 dark:border-teal-800' },
  '#1ead5b': { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-700 dark:text-green-400', border: 'border-green-200 dark:border-green-800' },

  // Blues
  '#3B82F6': { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800' },
  '#2c59c3': { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800' },
  '#0EA5E9': { bg: 'bg-sky-50 dark:bg-sky-900/20', text: 'text-sky-700 dark:text-sky-400', border: 'border-sky-200 dark:border-sky-800' },
  '#06B6D4': { bg: 'bg-cyan-50 dark:bg-cyan-900/20', text: 'text-cyan-700 dark:text-cyan-400', border: 'border-cyan-200 dark:border-cyan-800' },
  '#60A5FA': { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800' },

  // Purples
  '#8B5CF6': { bg: 'bg-violet-50 dark:bg-violet-900/20', text: 'text-violet-700 dark:text-violet-400', border: 'border-violet-200 dark:border-violet-800' },
  '#A855F7': { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-700 dark:text-purple-400', border: 'border-purple-200 dark:border-purple-800' },
  '#D946EF': { bg: 'bg-fuchsia-50 dark:bg-fuchsia-900/20', text: 'text-fuchsia-700 dark:text-fuchsia-400', border: 'border-fuchsia-200 dark:border-fuchsia-800' },

  // Reds
  '#EF4444': { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-700 dark:text-red-400', border: 'border-red-200 dark:border-red-800' },
  '#F87171': { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-600 dark:text-red-400', border: 'border-red-200 dark:border-red-800' },
  '#DC2626': { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-800 dark:text-red-400', border: 'border-red-300 dark:border-red-800' },

  // Oranges & Yellows
  '#F59E0B': { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' },
  '#F97316': { bg: 'bg-orange-50 dark:bg-orange-900/20', text: 'text-orange-700 dark:text-orange-400', border: 'border-orange-200 dark:border-orange-800' },
  '#EAB308': { bg: 'bg-yellow-50 dark:bg-yellow-900/20', text: 'text-yellow-700 dark:text-yellow-400', border: 'border-yellow-200 dark:border-yellow-800' },

  // Grays
  '#6B7280': { bg: 'bg-gray-50 dark:bg-gray-900/20', text: 'text-gray-600 dark:text-gray-400', border: 'border-gray-200 dark:border-gray-800' },
  '#9CA3AF': { bg: 'bg-gray-50 dark:bg-gray-900/20', text: 'text-gray-500 dark:text-gray-400', border: 'border-gray-200 dark:border-gray-800' },
  '#E5E7EB': { bg: 'bg-gray-50 dark:bg-gray-900/20', text: 'text-gray-600 dark:text-gray-400', border: 'border-gray-200 dark:border-gray-800' },

  // Pinks
  '#EC4899': { bg: 'bg-pink-50 dark:bg-pink-900/20', text: 'text-pink-700 dark:text-pink-400', border: 'border-pink-200 dark:border-pink-800' },
  '#F472B6': { bg: 'bg-pink-50 dark:bg-pink-900/20', text: 'text-pink-600 dark:text-pink-400', border: 'border-pink-200 dark:border-pink-800' },

  // Rose
  '#F43F5E': { bg: 'bg-rose-50 dark:bg-rose-900/20', text: 'text-rose-700 dark:text-rose-400', border: 'border-rose-200 dark:border-rose-800' },
};

/**
 * Default subtle color scheme for unknown colors
 */
export const DEFAULT_SUBTLE_COLORS: SubtleColorScheme = {
  bg: 'bg-gray-50 dark:bg-gray-900/20',
  text: 'text-gray-600 dark:text-gray-400',
  border: 'border-gray-200 dark:border-gray-800',
};

/**
 * Get subtle Tailwind CSS classes for a given hex color.
 * Returns default gray scheme if color is not found in the map.
 *
 * @param hexColor - Hex color code (e.g., '#22C55E')
 * @returns SubtleColorScheme with Tailwind CSS class names
 *
 * @example
 * const colors = getSubtleColorClasses('#22C55E');
 * // Returns: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' }
 */
export function getSubtleColorClasses(hexColor: string | undefined | null): SubtleColorScheme {
  if (!hexColor) {
    return DEFAULT_SUBTLE_COLORS;
  }

  // Normalize to uppercase for comparison
  const normalizedColor = hexColor.toUpperCase();

  // Check for exact match
  const exactMatch = HEX_TO_SUBTLE_COLORS[hexColor] || HEX_TO_SUBTLE_COLORS[normalizedColor];
  if (exactMatch) {
    return exactMatch;
  }

  // Try lowercase match
  const lowerMatch = HEX_TO_SUBTLE_COLORS[hexColor.toLowerCase()];
  if (lowerMatch) {
    return lowerMatch;
  }

  return DEFAULT_SUBTLE_COLORS;
}

/**
 * Solid color schemes for more prominent displays
 */
export interface SolidColorScheme {
  bg: string;
  text: string;
  hoverBg: string;
}

/**
 * Map of status types to their solid color schemes - Premium refined colors
 */
export const STATUS_TYPE_COLORS: Record<string, SolidColorScheme> = {
  // Success states - Emerald tones
  ACTIVE: { bg: 'bg-emerald-500/90', text: 'text-white', hoverBg: 'hover:bg-emerald-500' },
  COMPLETED: { bg: 'bg-emerald-500/90', text: 'text-white', hoverBg: 'hover:bg-emerald-500' },
  APPROVED: { bg: 'bg-emerald-500/90', text: 'text-white', hoverBg: 'hover:bg-emerald-500' },

  // In progress states - Sky blue tones
  IN_PROGRESS: { bg: 'bg-sky-500/90', text: 'text-white', hoverBg: 'hover:bg-sky-500' },
  PENDING: { bg: 'bg-sky-500/90', text: 'text-white', hoverBg: 'hover:bg-sky-500' },

  // Warning states - Amber tones
  ON_HOLD: { bg: 'bg-amber-500/90', text: 'text-white', hoverBg: 'hover:bg-amber-500' },
  DRAFT: { bg: 'bg-slate-400/90', text: 'text-white', hoverBg: 'hover:bg-slate-400' },

  // Error/cancel states
  CANCELED: { bg: 'bg-slate-400/80', text: 'text-white', hoverBg: 'hover:bg-slate-400' },
  CANCELLED: { bg: 'bg-slate-400/80', text: 'text-white', hoverBg: 'hover:bg-slate-400' },
  REJECTED: { bg: 'bg-rose-500/90', text: 'text-white', hoverBg: 'hover:bg-rose-500' },

  // Neutral states
  NOT_STARTED: { bg: 'bg-slate-200', text: 'text-slate-600', hoverBg: 'hover:bg-slate-300' },
  NEW: { bg: 'bg-slate-200', text: 'text-slate-600', hoverBg: 'hover:bg-slate-300' },
  INACTIVE: { bg: 'bg-slate-200', text: 'text-slate-600', hoverBg: 'hover:bg-slate-300' },
};

/**
 * Default solid color scheme for unknown statuses
 */
export const DEFAULT_SOLID_COLORS: SolidColorScheme = {
  bg: 'bg-slate-400/80',
  text: 'text-white',
  hoverBg: 'hover:bg-slate-400',
};

/**
 * Get solid Tailwind CSS classes for a given status type.
 *
 * @param statusType - Status type string (e.g., 'ACTIVE', 'IN_PROGRESS')
 * @returns SolidColorScheme with Tailwind CSS class names
 */
export function getSolidColorClasses(statusType: string | undefined | null): SolidColorScheme {
  if (!statusType) {
    return DEFAULT_SOLID_COLORS;
  }

  return STATUS_TYPE_COLORS[statusType.toUpperCase()] || DEFAULT_SOLID_COLORS;
}

/**
 * Priority colors for task/item priorities
 */
export const PRIORITY_COLORS: Record<string, SubtleColorScheme> = {
  URGENT: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  HIGH: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  MEDIUM: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  LOW: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  NONE: { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' },
};

/**
 * Get color scheme for a given priority level.
 *
 * @param priority - Priority level string
 * @returns SubtleColorScheme with Tailwind CSS class names
 */
export function getPriorityColorClasses(priority: string | undefined | null): SubtleColorScheme {
  if (!priority) {
    return PRIORITY_COLORS.NONE;
  }

  return PRIORITY_COLORS[priority.toUpperCase()] || PRIORITY_COLORS.NONE;
}

/**
 * Tag color schemes - designed for legibility with subtle backgrounds and darker text
 * Maps hex colors to Tailwind CSS classes for consistent tag styling
 */
export const TAG_COLOR_MAP: Record<string, SubtleColorScheme> = {
  // Reds / Coral - #FF6B6B
  '#FF6B6B': { bg: 'bg-red-100 dark:bg-red-950/50', text: 'text-red-700 dark:text-red-300', border: 'border-red-300 dark:border-red-800' },
  '#ff6b6b': { bg: 'bg-red-100 dark:bg-red-950/50', text: 'text-red-700 dark:text-red-300', border: 'border-red-300 dark:border-red-800' },

  // Teal - #4ECDC4
  '#4ECDC4': { bg: 'bg-teal-100 dark:bg-teal-950/50', text: 'text-teal-700 dark:text-teal-300', border: 'border-teal-300 dark:border-teal-800' },
  '#4ecdc4': { bg: 'bg-teal-100 dark:bg-teal-950/50', text: 'text-teal-700 dark:text-teal-300', border: 'border-teal-300 dark:border-teal-800' },

  // Blue - #45B7D1
  '#45B7D1': { bg: 'bg-sky-100 dark:bg-sky-950/50', text: 'text-sky-700 dark:text-sky-300', border: 'border-sky-300 dark:border-sky-800' },
  '#45b7d1': { bg: 'bg-sky-100 dark:bg-sky-950/50', text: 'text-sky-700 dark:text-sky-300', border: 'border-sky-300 dark:border-sky-800' },

  // Green / Mint - #96CEB4
  '#96CEB4': { bg: 'bg-emerald-100 dark:bg-emerald-950/50', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-300 dark:border-emerald-800' },
  '#96ceb4': { bg: 'bg-emerald-100 dark:bg-emerald-950/50', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-300 dark:border-emerald-800' },

  // Yellow - #FFEAA7
  '#FFEAA7': { bg: 'bg-amber-100 dark:bg-amber-950/50', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-300 dark:border-amber-800' },
  '#ffeaa7': { bg: 'bg-amber-100 dark:bg-amber-950/50', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-300 dark:border-amber-800' },

  // Plum - #DDA0DD
  '#DDA0DD': { bg: 'bg-fuchsia-100 dark:bg-fuchsia-950/50', text: 'text-fuchsia-700 dark:text-fuchsia-300', border: 'border-fuchsia-300 dark:border-fuchsia-800' },
  '#dda0dd': { bg: 'bg-fuchsia-100 dark:bg-fuchsia-950/50', text: 'text-fuchsia-700 dark:text-fuchsia-300', border: 'border-fuchsia-300 dark:border-fuchsia-800' },

  // Mint - #98D8C8
  '#98D8C8': { bg: 'bg-green-100 dark:bg-green-950/50', text: 'text-green-700 dark:text-green-300', border: 'border-green-300 dark:border-green-800' },
  '#98d8c8': { bg: 'bg-green-100 dark:bg-green-950/50', text: 'text-green-700 dark:text-green-300', border: 'border-green-300 dark:border-green-800' },

  // Light Yellow - #F7DC6F
  '#F7DC6F': { bg: 'bg-yellow-100 dark:bg-yellow-950/50', text: 'text-yellow-700 dark:text-yellow-300', border: 'border-yellow-300 dark:border-yellow-800' },
  '#f7dc6f': { bg: 'bg-yellow-100 dark:bg-yellow-950/50', text: 'text-yellow-700 dark:text-yellow-300', border: 'border-yellow-300 dark:border-yellow-800' },

  // Light Purple - #BB8FCE
  '#BB8FCE': { bg: 'bg-purple-100 dark:bg-purple-950/50', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-300 dark:border-purple-800' },
  '#bb8fce': { bg: 'bg-purple-100 dark:bg-purple-950/50', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-300 dark:border-purple-800' },

  // Light Blue - #85C1E9
  '#85C1E9': { bg: 'bg-blue-100 dark:bg-blue-950/50', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-300 dark:border-blue-800' },
  '#85c1e9': { bg: 'bg-blue-100 dark:bg-blue-950/50', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-300 dark:border-blue-800' },
};

/**
 * Default tag color scheme
 */
export const DEFAULT_TAG_COLORS: SubtleColorScheme = {
  bg: 'bg-slate-100 dark:bg-slate-800/50',
  text: 'text-slate-700 dark:text-slate-300',
  border: 'border-slate-300 dark:border-slate-700',
};

/**
 * Get Tailwind CSS classes for a tag's color.
 * Maps hex colors to legible, theme-consistent color schemes.
 *
 * @param hexColor - Hex color code from the tag definition
 * @returns SubtleColorScheme with Tailwind CSS class names
 *
 * @example
 * const colors = getTagColorClasses('#4ECDC4');
 * // Returns: { bg: 'bg-teal-100 dark:bg-teal-950/50', text: 'text-teal-700 dark:text-teal-300', border: 'border-teal-300 dark:border-teal-800' }
 */
export function getTagColorClasses(hexColor: string | undefined | null): SubtleColorScheme {
  if (!hexColor) {
    return DEFAULT_TAG_COLORS;
  }

  // Try exact match first
  const exactMatch = TAG_COLOR_MAP[hexColor];
  if (exactMatch) {
    return exactMatch;
  }

  // Try lowercase match
  const lowerMatch = TAG_COLOR_MAP[hexColor.toLowerCase()];
  if (lowerMatch) {
    return lowerMatch;
  }

  // Fallback: try to match to closest color family based on hex hue
  const fallbackScheme = getClosestTagColorScheme(hexColor);
  return fallbackScheme || DEFAULT_TAG_COLORS;
}

/**
 * Get closest tag color scheme based on hex color hue analysis
 */
function getClosestTagColorScheme(hexColor: string): SubtleColorScheme | null {
  // Parse hex to RGB
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate rough hue
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  if (max === min) {
    // Grayscale
    return { bg: 'bg-slate-100 dark:bg-slate-800/50', text: 'text-slate-700 dark:text-slate-300', border: 'border-slate-300 dark:border-slate-700' };
  }

  let hue = 0;
  if (max === r) {
    hue = ((g - b) / (max - min)) * 60;
  } else if (max === g) {
    hue = (2 + (b - r) / (max - min)) * 60;
  } else {
    hue = (4 + (r - g) / (max - min)) * 60;
  }
  if (hue < 0) hue += 360;

  // Map hue to color scheme
  if (hue < 15 || hue >= 345) {
    // Red
    return { bg: 'bg-red-100 dark:bg-red-950/50', text: 'text-red-700 dark:text-red-300', border: 'border-red-300 dark:border-red-800' };
  } else if (hue < 45) {
    // Orange
    return { bg: 'bg-orange-100 dark:bg-orange-950/50', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-300 dark:border-orange-800' };
  } else if (hue < 70) {
    // Yellow
    return { bg: 'bg-amber-100 dark:bg-amber-950/50', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-300 dark:border-amber-800' };
  } else if (hue < 150) {
    // Green
    return { bg: 'bg-emerald-100 dark:bg-emerald-950/50', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-300 dark:border-emerald-800' };
  } else if (hue < 195) {
    // Teal/Cyan
    return { bg: 'bg-teal-100 dark:bg-teal-950/50', text: 'text-teal-700 dark:text-teal-300', border: 'border-teal-300 dark:border-teal-800' };
  } else if (hue < 255) {
    // Blue
    return { bg: 'bg-blue-100 dark:bg-blue-950/50', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-300 dark:border-blue-800' };
  } else if (hue < 285) {
    // Purple
    return { bg: 'bg-purple-100 dark:bg-purple-950/50', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-300 dark:border-purple-800' };
  } else {
    // Pink/Magenta
    return { bg: 'bg-fuchsia-100 dark:bg-fuchsia-950/50', text: 'text-fuchsia-700 dark:text-fuchsia-300', border: 'border-fuchsia-300 dark:border-fuchsia-800' };
  }
}
